import ProgressBar from '@ramonak/react-progress-bar';
import SystemHelper from 'features/System/systemHelper';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormInput, {
  KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import ButtonFilterDropdown from 'general/components/quybka/Buttons/ButtonFilterDropdown';
import DropdownMultiChoiceView from 'general/components/quybka/Others/DropdownMultiChoiceView';
import AppColors from 'general/constants/AppColors';
import AppConfigs from 'general/constants/AppConfigs';
import AppRoute from 'general/constants/AppRoute';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import useWindowSize from 'hooks/useWindowSize';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import EnergyBillListFilterView from './components/EnergyBillListFilterView';
import useAppConfig from 'hooks/useAppConfig';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import AppConstants from 'general/constants/AppConstants';
import useCharge from 'features/Charge/useCharge';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import ChargeHelper from 'features/Charge/chargeHelper';

EnergyBillListView.propTypes = { showFullUI: PropTypes.bool, prefKey: PropTypes.string, agencyId: PropTypes.string };

const defaultColumnIds = [
  'MONTH',
  'STATION_CODE',
  'STATION_NAME',
  'TOTAL_ENERGY',
  'DIFFERENCE',
  'AMOUNT_BEFORE_TAX',
  'TAX',
  'AMOUNT_AFTER_TAX',
  'INVOICE_FROM_ELECTRICITY',
  'STATUS',
  'MANAGEMENT_OBJECT',
  'AGENCY',
  'DATE',
];

function EnergyBillListView({ showFullUI = true, prefKey, agencyId }) {
  // MARK: ---- PROPERTIES ----
  const router = useRouter();
  const appConfig = useAppConfig();
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const [filter, setFilter] = useState();
  useEffect(() => {
    if (Utils.isValid(prefKey)) {
      setFilter(SystemHelper.Filter.energyBillList);
    } else {
      setFilter({
        page: 0,
        limit: AppConfigs.defaultPageLimit,
        q: '',
        month: moment().format('YYYY-MM'),
        status: '',
        landlordId: agencyId,
      });
    }
  }, [prefKey, agencyId]);
  const [energyBillList, setEnergyBillList] = useState([]);
  const [monthlyEnergyList, setMonthlyEnergyList] = useState([]);
  const [total, setTotal] = useState(0);
  const [month, setMonth] = useState('');
  useEffect(() => {
    if (Utils.isValid(filter)) {
      const params = _.pickBy(
        filter,
        (value) => value !== undefined && value !== null && value !== ''
      );
      SystemHelper.API.EnergyBill.requestGetAll(params)
        .then((res) => {
          setEnergyBillList(res?.energyBills);
          setTotal(res?.total);
          setMonth(res?.month ?? params?.month);
        })
        .catch((err) => { });
    }
  }, [filter]);
  useEffect(() => {
    if (Utils.isValid(filter)) {
      ChargeHelper.API.Dashboard.requestGetMonthlyStationEnergy({ month: filter?.month })
        .then((res) => {
          setMonthlyEnergyList(res);
        })
        .catch((err) => { });
    }
  }, [filter]);
  const fullColumns = useMemo(() => {
    return [
      {
        id: 'STATION_CODE',
        name: UIHelper.buildTableColumn('Mã trạm sạc'),
        selector: (row) => (
          <span
            className="cursor-pointer hover-opacity-60"
            onClick={() => {
              Utils.openInNewTab(`${AppRoute.stationList}/${row?.station?.stationId}`);
            }}
          >
            {row?.station?.stationCode}
          </span>
        ),
        width: '140px',
        style: tableStyle.columns.link,
      },
      {
        id: 'MONTH',
        name: UIHelper.buildTableColumn('Kỳ hóa đơn'),
        selector: (row) =>
          Utils.isValid(row?.month) ? Utils.formatDateTimeToString(row?.month, 'MM/YYYY') : '',
        width: '110px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATION_NAME',
        name: UIHelper.buildTableColumn('Tên trạm sạc'),
        selector: (row) => (
          <span
            className="cursor-pointer hover-opacity-60"
            onClick={() => {
              Utils.openInNewTab(`${AppRoute.stationList}/${row?.station?.stationId}`);
            }}
          >
            {row?.station?.name}
          </span>
        ),
        minWidth: '240px',
        style: tableStyle.columns.link,
      },
      {
        id: 'TOTAL_ENERGY',
        name: UIHelper.buildTableColumn('Sản lượng điện tiêu thụ', 'kWh'),
        selector: (row) => monthlyEnergyList?.find(energy => energy?.stationId === row?.station?.stationId)?.totalEnergy,
        width: '180px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'DIFFERENCE',
        name: UIHelper.buildTableColumn('Chênh lệch với hệ thống', '%'),
        selector: (row) => {
          // const totalEnergyUsed = row.energyAccounts.reduce((sum, account) => {
          //   const billEnergy = account.energyBills.reduce((subSum, bill) => subSum + (bill.evnEnergyUsed || 0), 0);
          //   return sum + billEnergy;
          // }, 0);
          // return row?.totalEnergy ? (totalEnergyUsed - row?.totalEnergy) / row?.totalEnergy * 100 : 0;
          return 0;
        },
        width: '180px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'AMOUNT_BEFORE_TAX',
        name: UIHelper.buildTableColumn('HĐ tiền điện trước thuế', 'VNĐ'),
        selector: (row) =>
          Utils.isValid(row?.amountBeforeTax)
            ? Utils.formatNumber(row?.amountBeforeTax)
            : 0,
        width: '220px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'TAX',
        name: UIHelper.buildTableColumn('Thuế suất', '%'),
        selector: (row) =>
          Utils.isValid(row?.taxRate) ? Utils.formatNumber(row?.taxRate * 100) : 0,
        width: '120px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'AMOUNT_AFTER_TAX',
        name: UIHelper.buildTableColumn('HĐ tiền điện sau thuế', 'VNĐ'),
        selector: (row) =>
          Utils.isValid(row?.amount) ? Utils.formatNumber(row?.taxRate * 100) : 0,
        width: '220px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'INVOICE_FROM_ELECTRICITY',
        name: UIHelper.buildTableColumn('Hoá đơn từ điện lực'),
        selector: (row) => {
          const attachedFiles = row?.energyAccounts?.[0]?.energyBills?.[0]?.evnInvoiceDocument || [];

          const documentArray = Array.isArray(attachedFiles) ? attachedFiles : attachedFiles ? [attachedFiles] : [];
          const documentCount = documentArray.length;

          return documentCount > 0 ? (
            <span
              style={{ cursor: 'pointer' }}
              onClick={() => {
                const firstDocument = documentArray[0];
                if (firstDocument) {
                  Utils.openInNewTab(Utils.getFullUrl(firstDocument));
                }
              }}
            >
              Xem chi tiết
            </span>
          ) : null;
        },
        width: '160px',
        style: tableStyle.columns.link,
      },
      {
        id: 'STATUS',
        name: UIHelper.buildTableColumn('Trạng thái hóa đơn'),
        selector: (row) => (
          <KTLabel
            text={SystemHelper.Utils.EnergyBill.getEnergyBillStatusName(
              row?.status
            )}
            isInline={true}
            size={KTLabelSize.medium}
            additionalClassName={`label-light-${SystemHelper.Utils.EnergyBill.getEnergyBillStatusColor(
              row?.status
            )}`}
          />
        ),
        width: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'MANAGEMENT_OBJECT',
        name: UIHelper.buildTableColumn('Đối tượng quản lý'),
        selector: (row) =>
          Utils.isValid(row?.investor?.agencyId)
            ? Utils.isValid(row?.landlord?.agencyId)
              ? 'Dự án HTKD'
              : row?.investor?.agencyId === appConfig?.groupAgencyId
                ? 'Dự án HTKD'
                : 'NĐT nhượng quyền'
            : 'Nhà điều hành',
        width: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'AGENCY',
        name: UIHelper.buildTableColumn('NĐT/Chủ mặt bằng'),
        selector: (row) => (
          <span
            className="cursor-pointer hover-opacity-60"
            onClick={() => {
              if (Utils.isValid(row?.investor?.agencyId) && !Utils.isValid(row?.landlord?.agencyId) && row?.investor?.agencyId !== appConfig?.groupAgencyId) {
                Utils.openInNewTab(`/investor/${row?.investor?.agencyId}`);
              } else if (Utils.isValid(row?.landlord?.agencyId)) {
                Utils.openInNewTab(`/landlord/${row?.landlord?.agencyId}`);
              }
            }}
          >
            <KTTooltip offset={[0, 0]}
              text={
                Utils.isValid(row?.investor?.agencyId) && !Utils.isValid(row?.landlord?.agencyId) && row?.investor?.agencyId !== appConfig?.groupAgencyId
                  ? row?.investor?.name
                  : row?.landlord?.name
              }>
              <span>
                {Utils.isValid(row?.investor?.agencyId) && !Utils.isValid(row?.landlord?.agencyId) && row?.investor?.agencyId !== appConfig?.groupAgencyId
                  ? row?.investor?.name
                  : row?.landlord?.name
                }
              </span>
            </KTTooltip>
          </span>
        ),
        width: '240px',
        style: tableStyle.columns.link,
      },
      {
        id: 'DATE',
        name: UIHelper.buildTableColumn('Ngày gửi hóa đơn'),
        selector: (row) => Utils.formatDateTimeToString(row?.energyAccounts?.[0]?.energyBills?.[0]?.createdAt, 'DD/MM/YYYY - HH:mm'),
        width: '180px',
        style: tableStyle.columns.normal,
      },
      {
        name: UIHelper.buildTableColumn('Thao tác'),
        width: '80px',
        center: true,
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        cell: (row) => {
          return UIHelper.buildMoreActionsButton(buildMoreActions(row), true);
        },
      },
    ];
  }, [user, month]);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns?.map((row) => row?.id).filter(Boolean) || []
  );
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
    saveChanges,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);
  const windowSize = useWindowSize();

  const { managementObjectOptions } = useCharge();
  const [selectedManagementObject, setSelectedManagementObject] = useState('');
  const [selectedAgency, setSelectedAgency] = useState();

  // MARK: ---- METHODS ----
  const handleRefresh = (newFilter) => {
    if (Utils.isValid(prefKey)) {
      SystemHelper.Filter.energyBillList = {
        ...SystemHelper.Filter.energyBillList,
        ...newFilter,
      };
      setFilter({ ...SystemHelper.Filter.energyBillList });
    } else {
      setFilter(newFilter);
    }
  };

  const buildMoreActions = (row) => {
    const actions = [];
    // xem chi tiết
    actions.push({
      text: 'Xem chi tiết',
      iconElement: <i className="fa-regular fa-eye" style={{ fontSize: '1.1rem' }} />,
      click: () => {
        if (showFullUI) {
          router.navigate(`${row?.stationId}?month=${month}`);
        } else {
          Utils.openInNewTab(`${AppRoute.energyBillList}/${row?.stationId}?month=${month}`);
        }
      },
    });
    return actions;
  };

  return (
    <div>
      {UIHelper.buildCard(
        <div>
          <div className="pb-4">
            {/* header */}
            {showFullUI ? (
              <div className="px-4 pt-4 pb-4 d-flex align-items-center justify-content-between">
                {/* title */}
                <div className="d-flex flex-column">
                  <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                    Danh sách hóa đơn tiền điện
                  </span>
                  <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                    Tổng:{' '}
                    <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                      {Utils.formatNumber(total)} hoá đơn
                    </span>
                  </span>
                </div>

                {/* actions */}
              </div>
            ) : (
              <div className="mb-4"></div>
            )}

            {/* table */}
            <div>
              {/* filter */}
              <div className="row m-0 p-0 px-2">
                {/* tim kiem */}
                {windowSize.width >= 768 && (
                  <div className="col mb-4 px-2">
                    {UIHelper.buildFormLabel('Tìm kiếm')}
                    <KTFormSearch
                      name="search"
                      placeholder="Theo mã trạm sạc, tên, địa chỉ..."
                      value={filter?.q}
                      onSubmit={(text) => {
                        handleRefresh({ ...filter, q: text, page: 0 });
                      }}
                    />
                  </div>
                )}
                {/* ky hoa don */}
                {windowSize.width >= 1120 && (
                  <div className="col mb-4 px-2">
                    {UIHelper.buildFormLabel('Kỳ hoá đơn')}
                    <KTFormInput
                      name="month"
                      type={KTFormInputType.month}
                      value={filter?.month}
                      onChange={(value) => {
                        handleRefresh({ ...filter, month: value, page: 0 });
                      }}
                    />
                  </div>
                )}
                {/* trang thai */}
                {/* {windowSize.width >= 1368 && (
                  <div className="col-3 mb-4 px-2">
                    {UIHelper.buildFormLabel('Trạng thái hoá đơn')} */}
                {/* <KTBSDropdown
                      name="status"
                      toggleElement={
                        <button
                          className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                          type="button"
                        >
                          {_.find(
                            SystemHelper.Data.EnergyBillStatusOptions,
                            (item) => item.value === filter?.status
                          )?.name ?? 'Tất cả'}
                        </button>
                      }
                      selectedValue={filter?.status}
                      dropdownMenuItems={[
                        { name: 'Tất cả', value: '' },
                        ...SystemHelper.Data.EnergyBillStatusOptions,
                      ]}
                      dropdownMenuClassName="overflow-auto w-100"
                      dropdownMenuStyle={{ maxHeight: '30vh' }}
                      onChange={(value) => {
                        handleRefresh({ ...filter, status: value, page: 0 });
                      }}
                      isDropdownForm={true}
                    /> */}
                {/* <DropdownMultiChoiceView
                      items={SystemHelper.Data.EnergyBillStatusOptions}
                      initialSelectedValues={filter?.status?.split(',') ?? []}
                      onChange={(items) => {
                        handleRefresh({
                          ...filter,
                          status: items?.join(','),
                          page: 0,
                        });
                      }}
                    />
                  </div>
                )} */}
                {/* doi tuong quan ly */}
                {windowSize.width >= 1630 && (
                  <div className="col mb-4 p-0 px-2">
                    {UIHelper.buildFormLabel('Đối tượng quản lý')}
                    <KTBSDropdown
                      name="managementObject_filter"
                      toggleElement={
                        <button
                          className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
                          type="button"
                        >
                          {_.find(managementObjectOptions, (item) => item.value === selectedManagementObject)
                            ?.name ?? 'Tất cả'}
                        </button>
                      }
                      selectedValue={selectedManagementObject}
                      dropdownMenuItems={[{ name: 'Tất cả', value: '' }, ...managementObjectOptions]}
                      dropdownMenuClassName="overflow-auto w-100"
                      dropdownMenuStyle={{ maxHeight: '30vh' }}
                      onChange={(value) => {
                        setSelectedManagementObject(value);
                        setSelectedAgency(null);
                        handleRefresh({
                          ...filter,
                          agencyId: '',
                          landlordId: '',
                          page: 0
                        });
                      }}
                      isDropdownForm={true}
                    />
                  </div>
                )}
                {/* thiet lap cot */}
                <div className="col mb-4 px-2 d-flex align-items-end gap-3">
                  {/* bo loc */}
                  <ButtonFilterDropdown
                    dropdownContent={
                      <EnergyBillListFilterView
                        onCancel={(newFilter) => {
                          handleRefresh(newFilter);
                        }}
                        onApply={(newFilter) => {
                          handleRefresh(newFilter);
                        }}
                        initialFilter={filter}
                      />
                    }
                  />
                  <DropdownTableConfigColumn
                    columnIds={columnIds}
                    fullColumns={fullColumns}
                    fullColumnIds={fullColumnsIdSorted}
                    onChangeCheckboxValue={onChangeCheckboxValue}
                    resetDefaultTableColumn={resetDefaultTableColumn}
                    onChangeFullColumnIds={(ids) => {
                      setFullColumnIds(ids);
                      const key = prefKey + '_FULL_COLUMN_IDS';
                      localStorage.setItem(key, ids.join('|'));
                    }}
                    onSelectAll={onSelectAll}
                    onUnSelectAll={onUnSelectAll}
                    saveChanges={saveChanges}
                  />
                </div>
              </div>

              <div className="px-4">
                <KTTable
                  columns={columns}
                  items={energyBillList}
                  selectableRows={false}
                  striped={false}
                  compactGrid={false}
                  onClickedRow={(row) => { }}
                  onSelectedRowsChanged={(rows) => { }}
                  onDoubleClickedRow={(row) => { }}
                  noDataComponent={UIHelper.buildEmptyNoData(
                    'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                  )}
                />
              </div>
            </div>

            {/* pagination */}
            <div className="px-4 mt-2">
              <KTPagination
                currentPage={filter?.page + 1}
                rowCount={total}
                rowsPerPage={filter?.limit}
                currentItemInPageCount={energyBillList?.length}
                onChangePage={(newPage) => {
                  const iNewPage = parseInt(newPage);
                  handleRefresh({ ...filter, page: iNewPage - 1 });
                }}
                onChangeRowsPerPage={(newRowsPerPage) => {
                  const iNewRowsPerPage = parseInt(newRowsPerPage);
                  handleRefresh({ ...filter, limit: iNewRowsPerPage, page: 0 });
                }}
                loading={false}
                color={KTPaginationColor.success}
                appendText="hoá đơn"
              />
            </div>
          </div>
        </div>,
        false,
        !showFullUI
      )}
    </div>
  );
}

export default EnergyBillListView;
